import { useEffect, useRef, useState } from 'react';
import type { GameType } from '@big2/shared';
import { useGameState } from '../net/GameProvider.js';
import { useI18n } from '../i18n.js';
import { useSound } from '../sound.js';
import { getUserAvatar } from '../utils/avatar.js';
import { AppLogo } from './AppLogo.js';
import { Icon } from './Icon.js';
import { DaaluuColorPicker } from './game/daaluu/DaaluuColorPicker.js';
import { TsaiLeaveWarningModal } from './game/common/TsaiLeaveWarningModal.js';
import { trackEvent } from '../analytics.js';
import type { AppRoute } from '../router.js';
import type { AuthViewMode } from './profile/AuthForms.js';

export interface AppHeaderProps {
  activeGameType: GameType;
  headerCopied?: boolean;
  onCopyHeaderCode?: () => void;
  onOpenAuth: (view?: AuthViewMode) => void;
  onOpenFeedback?: () => void;
  navigate: (next: AppRoute) => void;
}

export function AppHeader({
  activeGameType,
  headerCopied: _headerCopied,
  onCopyHeaderCode: _onCopyHeaderCode,
  onOpenAuth,
  onOpenFeedback,
  navigate,
}: AppHeaderProps) {
  const { state, actions } = useGameState();
  const { lang, setLang, t } = useI18n();
  const { muted, toggleMute } = useSound();
  const isMn = lang === 'mn';
  const isHost = Boolean(state.lobby?.hostId && state.lobby.hostId === state.playerId);
  const inGame = Boolean(state.game && (state.game.phase === 'playing' || Boolean(state.lastGameOver)));
  const isPlayingActiveTsai = Boolean(
    state.game?.phase === 'playing' &&
    !state.lastGameOver &&
    state.lobby?.config?.mode === 'tsai'
  );
  const [pendingLeaveRoute, setPendingLeaveRoute] = useState<AppRoute | null>(null);

  const menuRef = useRef<HTMLDetailsElement>(null);

  const closeMenu = () => {
    menuRef.current?.removeAttribute('open');
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (!menuRef.current || !menuRef.current.open) return;
      if (!menuRef.current.contains(event.target as Node)) {
        menuRef.current.removeAttribute('open');
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && menuRef.current?.open) {
        menuRef.current.removeAttribute('open');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleOpenProfile = () => {
    if (state.profile) {
      if (inGame) {
        if (isPlayingActiveTsai) {
          setPendingLeaveRoute({ type: 'profile', tab: 'profile' });
          return;
        }
        const leave = window.confirm(
          isMn
            ? 'Тоглоомоос гарч профайл хуудас руу шилжих үү?'
            : 'Leave the current game and go to your profile?'
        );
        if (!leave) return;
        actions.leaveRoom();
      }
      navigate({ type: 'profile', tab: 'profile' });
    } else {
      onOpenAuth('signup');
    }
  };

  return (
    <header className="app-header" data-connection-status={state.connectionStatus}>
      <div className="app-header-left">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            if (state.roomId) {
              if (isPlayingActiveTsai) {
                setPendingLeaveRoute({ type: 'home' });
                return;
              }
              const leave = window.confirm(
                isMn
                  ? 'Өрөөнөөс гарч эхлэл хуудас руу буцах уу?'
                  : 'Leave the current room and return to the home page?'
              );
              if (!leave) return;
              actions.leaveRoom();
            }
            navigate({ type: 'home' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="app-brand-link"
          aria-label={isMn ? 'Арван гурав Эхлэл' : 'Big Two Home'}
        >
          <AppLogo size={28} />
          <span className="app-title">{t.appTitle}</span>
        </a>
      </div>

      <div className="app-header-right">
        {/* 1. Primary Account / Log In Button First */}
        {state.profile ? (() => {
          const headerAvatar = getUserAvatar(state.profile.id, state.profile.displayName);
          return (
            <button
              type="button"
              onClick={handleOpenProfile}
              className="header-auth-btn header-profile-btn"
              title={`${state.profile.displayName} · 🍵 ${state.profile.tsaiBalance}`}
              aria-label={`${state.profile.displayName}, 🍵 ${state.profile.tsaiBalance}`}
            >
              <span
                className="header-profile-avatar"
                style={{
                  background: headerAvatar.gradient,
                  borderColor: headerAvatar.borderColor,
                  boxShadow: `0 0 6px ${headerAvatar.glow}`,
                }}
                aria-hidden="true"
              >
                {headerAvatar.initial}
              </span>
              <span className="header-tsai-count">🍵 {state.profile.tsaiBalance}</span>
              <span className="sr-only">{state.profile.displayName}</span>
            </button>
          );
        })() : (
          <button
            type="button"
            onClick={() => onOpenAuth('signin')}
            className="header-auth-btn header-login-btn"
            title={isMn ? 'Нэвтрэх' : 'Log In'}
          >
            <span>{isMn ? 'Нэвтрэх' : 'Log In'}</span>
          </button>
        )}

        <details ref={menuRef} className="header-tools">
          <summary aria-label={isMn ? 'Цэс' : 'Menu'} title={isMn ? 'Цэс' : 'Menu'}>
            <Icon name="menu" size={20} />
          </summary>

          <div className="header-menu">
            {/* Section 1: Account & Auth (Always First) */}
            <div className="header-menu-group">
              <span className="header-menu-label">
                {isMn ? 'Бүртгэл' : 'Account'}
              </span>

              {state.profile ? (() => {
                const menuAvatar = getUserAvatar(state.profile.id, state.profile.displayName);
                return (
                  <button
                    type="button"
                    className="header-menu-item"
                    onClick={() => {
                      closeMenu();
                      handleOpenProfile();
                    }}
                  >
                    <span
                      className="header-profile-avatar"
                      style={{
                        background: menuAvatar.gradient,
                        borderColor: menuAvatar.borderColor,
                        boxShadow: `0 0 6px ${menuAvatar.glow}`,
                      }}
                      aria-hidden="true"
                    >
                      {menuAvatar.initial}
                    </span>
                    <span className="header-menu-profile-name">
                      {state.profile.tier === 'paid' ? '👑 ' : ''}
                      {state.profile.displayName}
                    </span>
                    <span className="header-menu-tsai">🍵 {state.profile.tsaiBalance}</span>
                  </button>
                );
              })() : (
                <button
                  type="button"
                  className="header-menu-item header-menu-guest-btn"
                  onClick={() => {
                    closeMenu();
                    onOpenAuth('signup');
                  }}
                >
                  <span>👤</span>
                  <span>{t.profileButtonGuest}</span>
                </button>
              )}

              {state.profile && (
                <a
                  className="header-menu-item header-payment-link"
                  href="/payments"
                  onClick={(event) => {
                    event.preventDefault();
                    closeMenu();
                    navigate({ type: 'payments' });
                  }}
                >
                  <span className="header-menu-icon" aria-hidden="true">♣</span>
                  <span>{isMn ? 'Төлбөрийн түүх' : 'Payment history'}</span>
                </a>
              )}

              {state.profile?.isAdmin && (
                <button
                  type="button"
                  className="header-menu-item is-accent"
                  onClick={() => {
                    closeMenu();
                    navigate({ type: 'admin' });
                  }}
                >
                  <span className="header-menu-icon" aria-hidden="true">❖</span>
                  <span>{isMn ? 'Админ самбар' : 'Admin Panel'}</span>
                </button>
              )}
            </div>

            {/* Section 2: Active Room Controls (when inside a room) */}
            {state.roomId && (
              <div className="header-menu-group">
                <span className="header-menu-label">
                  {isMn ? 'Одоогийн өрөө' : 'Current Room'}
                </span>

                {activeGameType === 'daaluu' ? (
                  <div className="header-menu-daaluu-picker">
                    <DaaluuColorPicker />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="header-menu-item"
                    onClick={() => {
                      closeMenu();
                      navigate({ type: 'cardBacks' });
                    }}
                  >
                    <span className="header-menu-icon" aria-hidden="true">♠</span>
                    <span>{t.cardBacksButton}</span>
                  </button>
                )}

                {inGame && isHost && (
                  <button
                    type="button"
                    className="header-menu-item is-accent header-return-lobby-btn"
                    onClick={(event) => {
                      event.preventDefault();
                      closeMenu();
                      const confirmReturn = window.confirm(
                        isMn
                          ? 'Тоглоомыг зогсоож, өрөө рүүгээ буцах уу? Бүх тоглогчид өрөөнд үлдэх бөгөөд тохиргоог өөрчлөх боломжтой болно.'
                          : 'Return to lobby to change settings? All players will stay in the room and you can adjust the point limit and other settings.'
                      );
                      if (confirmReturn) {
                        actions.returnToLobby();
                      }
                    }}
                    title={isMn ? 'Өрөө рүү буцах (Тохиргоо засах)' : 'Return to lobby (Change settings)'}
                    aria-label={isMn ? 'Өрөө рүү буцах (Тохиргоо засах)' : 'Return to lobby (Change settings)'}
                  >
                    <Icon name="sliders-horizontal" size={15} />
                    <span>{isMn ? 'Өрөө рүү буцах (Тохиргоо)' : 'Return to Lobby (Settings)'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Section 3: Game Guide & Club Navigation */}
            <div className="header-menu-group">
              <span className="header-menu-label">
                {isMn ? 'Тоглоом ба Мэдээлэл' : 'Guide & Club'}
              </span>

              <button
                type="button"
                className="header-menu-item"
                onClick={() => {
                  closeMenu();
                  navigate({ type: 'rules', gameType: activeGameType });
                }}
              >
                <Icon name="list-ordered" size={15} />
                <span>{t.rulesButton}</span>
              </button>

              {(state.roomHistoryLinks?.length ?? 0) > 0 && (
                <button
                  type="button"
                  className="header-menu-item header-room-history-btn"
                  onClick={(event) => {
                    event.preventDefault();
                    closeMenu();
                    navigate({ type: 'history' });
                  }}
                >
                  <Icon name="rotate-ccw" size={15} />
                  <span>{isMn ? 'Өрөөний түүх' : 'Room history'}</span>
                </button>
              )}

              <button
                type="button"
                className="header-menu-item is-gold"
                onClick={() => {
                  closeMenu();
                  navigate({ type: 'info', page: 'subscription' });
                }}
              >
                <Icon name="crown" size={15} />
                <span>{isMn ? 'VIP ба Нөхцөл' : 'VIP & Policies'}</span>
              </button>

              {onOpenFeedback && (
                <button
                  type="button"
                  className="header-menu-item"
                  onClick={() => {
                    closeMenu();
                    onOpenFeedback();
                  }}
                  title={isMn ? 'Санал хүсэлт, алдаа мэдээлэх' : 'Send feedback or report an issue'}
                >
                  <Icon name="message-square" size={15} />
                  <span>{isMn ? 'Санал хүсэлт илгээх' : 'Feedback & Bug Report'}</span>
                </button>
              )}
            </div>

            {/* Section 4: Preferences (Sound & Language) */}
            <div className="header-menu-group">
              <span className="header-menu-label">
                {isMn ? 'Тохиргоо' : 'Preferences'}
              </span>

              <button
                type="button"
                className={`header-menu-item header-sound-btn${muted ? ' is-muted' : ''}`}
                onClick={() => {
                  trackEvent('sound_toggled', { muted: !muted });
                  toggleMute();
                }}
                title={muted ? (isMn ? 'Дуу нээх' : 'Unmute sound') : (isMn ? 'Дуу хаах' : 'Mute sound')}
                aria-label={muted ? 'Unmute sound' : 'Mute sound'}
              >
                <span>{muted ? '🔇' : '🔊'}</span>
                <span>{muted ? (isMn ? 'Дуу нээх' : 'Unmute sound') : (isMn ? 'Дуу хаах' : 'Mute sound')}</span>
              </button>

              <div className="header-lang-row">
                <span className="header-lang-label">
                  {isMn ? 'Хэл' : 'Language'}
                </span>
                <div className="header-lang-segment" role="group" aria-label={isMn ? 'Хэл' : 'Language'}>
                  <button
                    type="button"
                    className={`header-lang-pill${lang === 'mn' ? ' is-active' : ''}`}
                    onClick={() => {
                      setLang('mn');
                      trackEvent('language_changed', { lang: 'mn' });
                    }}
                  >
                    🇲🇳 МН
                  </button>
                  <button
                    type="button"
                    className={`header-lang-pill${lang === 'en' ? ' is-active' : ''}`}
                    onClick={() => {
                      setLang('en');
                      trackEvent('language_changed', { lang: 'en' });
                    }}
                  >
                    🇬🇧 EN
                  </button>
                </div>
              </div>
            </div>

            {/* Section 5: Destructive Room Exit (Bottom of menu when in a room) */}
            {state.roomId && (
              <div className="header-menu-group is-footer-group">
                <button
                  type="button"
                  className="header-menu-item is-danger header-leave-room-btn"
                  onClick={(event) => {
                    event.preventDefault();
                    closeMenu();
                    if (isPlayingActiveTsai) {
                      setPendingLeaveRoute({ type: 'home' });
                      return;
                    }
                    actions.leaveRoom();
                    navigate({ type: 'home' });
                  }}
                  title={t.leave}
                  aria-label={t.leave}
                >
                  <Icon name="log-out" size={15} />
                  <span>{t.leave}</span>
                </button>
              </div>
            )}
          </div>
        </details>
      </div>
      {pendingLeaveRoute && (
        <TsaiLeaveWarningModal
          seats={state.lobby?.config.seats ?? 4}
          gameType={state.lobby?.config.gameType ?? activeGameType}
          onStay={() => setPendingLeaveRoute(null)}
          onConfirmAbandon={() => {
            const target = pendingLeaveRoute;
            setPendingLeaveRoute(null);
            actions.leaveRoom();
            navigate(target);
          }}
        />
      )}
    </header>
  );
}
